import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { Image as ImageIcon, Video as VideoIcon } from 'lucide-react'

/**
 * Clickable media chip (image OR video) with a hover-tooltip preview portaled
 * to body so it escapes card overflow/transform clipping. 350 ms enter delay
 * keeps it out of the way during fast cursor traversal.
 *
 * - kind === 'image': up to 4 thumbnails (single = 384×384, grid = 192×192).
 * - kind === 'video': a single muted, looping autoplay preview at 384×384.
 *
 * Clicking the chip opens the full lightbox (via `onView`).
 */
export function MediaPreviewChip({
  kind,
  urls,
  label,
  countTitle,
  onView,
  size = 'sm',
}: {
  kind: 'image' | 'video'
  urls: string[]
  label: string
  countTitle?: string
  onView: () => void
  size?: 'sm' | 'md'
}) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const timerRef = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    },
    [],
  )
  if (urls.length === 0) return null

  function enter(e: React.MouseEvent<HTMLButtonElement>) {
    const r = e.currentTarget.getBoundingClientRect()
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => setRect(r), 350)
  }
  function leave() {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setRect(null)
  }

  const isCompact = size === 'sm'
  const showCount = urls.length > 1
  const isVideo = kind === 'video'
  const POPOVER_HALF_WIDTH = 200
  const previewTopAbove = rect ? Math.max(8, rect.top - 8) : 0
  const flipBelow = rect ? rect.top < 440 : false

  const preview = rect ? (
    <div
      className="pointer-events-none fixed z-[80] rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-1.5 shadow-[var(--shadow-modal)]"
      style={{
        left: Math.min(
          window.innerWidth - POPOVER_HALF_WIDTH * 2 - 8,
          Math.max(8, rect.left + rect.width / 2 - POPOVER_HALF_WIDTH),
        ),
        ...(flipBelow
          ? { top: rect.bottom + 8 }
          : { top: previewTopAbove, transform: 'translateY(-100%)' }),
      }}
    >
      {isVideo ? (
        // First video, muted + autoplay + loop — gives a quick "what's in
        // it" peek without dragging audio context into a hover.
        <video
          key={urls[0]}
          src={urls[0]}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="h-96 w-96 rounded-md bg-black object-contain"
        />
      ) : (
        <>
          <div
            className={cn(
              'grid gap-1',
              urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
            )}
          >
            {urls.slice(0, 4).map((u) => (
              <img
                key={u}
                src={u}
                alt=""
                className={cn(
                  'rounded-md object-cover',
                  urls.length === 1 ? 'h-96 w-96' : 'h-48 w-48',
                )}
              />
            ))}
          </div>
          {urls.length > 4 && (
            <div className="mt-1 text-center text-[10px] text-[var(--color-text-dim)]">
              +{urls.length - 4}
            </div>
          )}
        </>
      )}
    </div>
  ) : null

  const Icon = isVideo ? VideoIcon : ImageIcon

  return (
    <>
      <button
        type="button"
        aria-label={label}
        title={countTitle ?? label}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onView()
        }}
        onMouseEnter={enter}
        onMouseLeave={leave}
        className={cn(
          'inline-flex shrink-0 items-center text-[var(--color-link)] transition-colors hover:bg-[var(--color-accent-bg)]',
          isCompact
            ? 'justify-center gap-0.5 rounded-md p-0.5'
            : 'gap-0.5 rounded-md px-1 py-0.5',
        )}
      >
        <Icon className={isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        {!isCompact && showCount && urls.length}
      </button>
      {preview && createPortal(preview, document.body)}
    </>
  )
}
